<template>
  <SfModal v-if="modalOpen" :scrollable-content="false" size="s" @close="close">
    <template #title>
      <span
        v-dompurify-html="
          I18n.t('reconciliation_text.copy_data.modal_title_html', {
            name: sanitizer(reconciliationTextName || '', {
              ALLOWED_TAGS: [],
              ALLOWED_ATTR: [],
            }),
            period: currentPeriodName,
          })
        "
      ></span>
    </template>
    <template #body>
      <div v-if="isLoading" class="sf-level sf-level-center">
        <div class="sf-spinner"></div>
      </div>
      <div v-else class="sf-grid sf-grid-fluid">
        <div>
          <div class="sf-level sf-below-3">
            <div class="sf-level-item">
              {{
                I18n.t('reconciliation_text.copy_data.modal_roll_data_forward')
              }}
            </div>
          </div>
          <div class="sf-level sf-below-1">
            <div class="sf-level-item sf-font-medium">
              {{ I18n.t('reconciliation_text.copy_data.pick_period') }}
            </div>
          </div>
          <div class="sf-level">
            <SfDropdown
              :dropdown-id="dropdownId"
              :is-open="isDropdownOpen"
              :is-full-width="true"
              variant="neutral"
              @dropdown-trigger-clicked="toggleDropdown"
              @dropdown-closed="closeDropdown"
            >
              <template #trigger>
                <span v-if="selectedLedger" class="sf-font-light">{{
                  selectedLedger.name
                }}</span>
                <span v-else class="sf-font-light"
                  ><i>{{
                    I18n.t('reconciliation_text.copy_data.select_period')
                  }}</i></span
                >
              </template>
              <template #menu>
                <div class="tw-max-h-[400px] tw-overflow-y-auto">
                  <SfDropdownItem
                    v-for="ledger in ledgers"
                    :key="ledger.id"
                    :is-selected="
                      !!selectedLedger && selectedLedger.id === ledger.id
                    "
                    @click="selectLedger(ledger)"
                  >
                    {{ ledger.name }}
                  </SfDropdownItem>
                </div>
              </template>
            </SfDropdown>
          </div>
        </div>
      </div>
    </template>

    <template #messages-bottom>
      <SfModalMessage v-if="copyError" variant="error">
        {{ I18n.t('reconciliation_text.copy_data.failed') }}
      </SfModalMessage>
      <SfModalMessage v-if="existingData" variant="error">
        <span
          v-dompurify-html="
            I18n.t('reconciliation_text.copy_data.warning', {
              period: currentPeriodName,
            })
          "
        ></span>
      </SfModalMessage>
      <SfModalMessage variant="neutral">
        <span
          v-dompurify-html="
            I18n.t('reconciliation_text.copy_data.bulk_copy_banner_html', {
              link: bulkCopyLink,
            })
          "
        ></span>
      </SfModalMessage>
    </template>

    <template #footer>
      <SfButtonDs
        :variant="existingData ? 'danger' : 'primary'"
        :disabled="!selectedLedger || requestSent"
        @click="copyLedger"
      >
        {{
          I18n.t('reconciliation_text.copy_data.yes_override', {
            period: currentPeriodName,
          })
        }}
      </SfButtonDs>
      <div class="sf-level sf-level-right">
        <SfButtonDs :disabled="requestSent" @click="close">
          {{ I18n.t('confirmation.cancel') }}
        </SfButtonDs>
      </div>
    </template>
  </SfModal>
</template>

<script setup lang="ts">
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { ref, computed } from 'vue'
import api from '../api/index'
import { SfModal, SfModalMessage } from '@app/shared/vue/components/sf-modal'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import sanitizer from '@app/adapters/sanitizer'
import { askUnreviewConfirmation } from '@app/shared/services/data-alteration-guard'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { dropdowns } from '@app/shared/types/dropdown'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()

interface Ledger {
  id: number
  name: string
}

const dropdownStore = useDropdown()
const dropdownId = dropdowns.COPY_RECONCILIATION_DATA

const modalOpen = ref(true)
const isLoading = ref(false)
const selectedLedger = ref<Ledger | null>(null)
const currentPeriodName = ref<string | null>(null)
const reconciliationTextName = ref<string | null>(null)
const existingData = ref(false)
const ledgers = ref<Ledger[]>([])
const ledgerId = ref<number | null>(null)
const reconciliationTextId = ref<number | null>(null)
const requestSent = ref(false)
const copyError = ref(false)

const isDropdownOpen = computed(
  () => dropdownStore.currentDropdownId === dropdownId
)

const bulkCopyLink = computed(() => {
  if (!ledgerId.value) return ''
  const params = {
    firm_id: window.Silverfin.currentFirmId,
    company_id: window.Silverfin.currentCompanyId,
    ledger_id: ledgerId.value,
  }
  return window.Silverfin.Routes.new_ledger_ledger_copier_path(params)
})

const openModal = ({
  ledgerId: ledgerIdParam,
  currentPeriodName: currentPeriodNameParam,
  reconciliationTextId: reconciliationTextIdParam,
  reconciliationTextName: reconciliationTextNameParam,
}: {
  ledgerId: number
  currentPeriodName: string
  reconciliationTextId: number
  reconciliationTextName: string
}) => {
  currentPeriodName.value = currentPeriodNameParam
  reconciliationTextName.value = reconciliationTextNameParam
  modalOpen.value = true
  isLoading.value = true
  ledgerId.value = ledgerIdParam
  reconciliationTextId.value = reconciliationTextIdParam

  api
    .getModalData({
      ledgerId: ledgerIdParam,
      reconciliationTextId: reconciliationTextIdParam,
    })
    .then((data) => {
      ledgers.value = data.ledgers
      existingData.value = data.existing_data
      isLoading.value = false

      if (data.last_ledger_with_data) {
        selectedLedger.value = data.last_ledger_with_data
      }
    })
}

const close = () => {
  if (!requestSent.value) {
    modalOpen.value = false
    dropdownStore.closeDropdown()
  }
}

const toggleDropdown = () => {
  dropdownStore.toggleDropdown(dropdownId)
}

const closeDropdown = () => {
  dropdownStore.closeDropdown()
}

const selectLedger = (ledger: Ledger) => {
  selectedLedger.value = ledger
  closeDropdown()
}

const copyLedger = async () => {
  const { confirmed } = await askUnreviewConfirmation()
  if (!confirmed) return

  doCopyLedger()
}

const doCopyLedger = () => {
  if (requestSent.value || !selectedLedger.value) return

  requestSent.value = true
  copyError.value = false

  // Both a copy and a refused copy answer with a Turbolinks visit that jQuery
  // runs, so the page replaces itself and the flash carries the outcome. Only a
  // request that never got an answer reaches the catch, and the copy can still
  // be running when it does.
  api
    .copyLedger({
      fromLedger: selectedLedger.value.id,
      reconciliationTextId: reconciliationTextId.value!,
      ledgerId: ledgerId.value!,
    })
    .catch(() => {
      requestSent.value = false
      copyError.value = true
    })
}

defineExpose({
  openModal,
})
</script>
